import { supabase } from '../lib/supabase';

export interface ApiKey {
  id: string;
  api_key: string;
  organisation_id: string;
  access_type: 'internal' | 'organization' | 'demo';
  is_active: boolean;
  expires_at?: string;
  rate_limit_per_hour?: number;
}

export interface ApiKeyResult {
  data: ApiKey | null;
  error: string | null;
}

/**
 * API Key Service
 * Handles fetching API keys for external API calls
 */
export class ApiKeyService {
  private static cache = new Map<string, { data: ApiKey; timestamp: number }>();
  private static cacheTimeout = 5 * 60 * 1000; // 5 minutes

  /**
   * Get API key for an organization
   */
  static async getApiKeyForOrganization(organisationId: string): Promise<ApiKeyResult> {
    try {
      const cacheKey = `api_key_${organisationId}`;
      const cached = this.cache.get(cacheKey);
      
      // Check cache first
      if (cached && Date.now() - cached.timestamp < this.cacheTimeout) {
        console.log('📦 Using cached API key for organization:', organisationId);
        return { data: cached.data, error: null };
      }

      console.log('🔄 Fetching fresh API key for organization:', organisationId);
      
      // First try to get internal access type
      let { data, error } = await supabase
        .from('api_keys')
        .select('*')
        .eq('organisation_id', organisationId)
        .eq('is_active', true)
        .eq('access_type', 'internal')
        .order('created_at', { ascending: false })
        .limit(1)
        .single();

      // If no internal key found, try any active key for the organization
      if (error && error.code === 'PGRST116') {
        console.log('🔄 No internal API key found, trying any active key for organization:', organisationId);
        const { data: fallbackData, error: fallbackError } = await supabase
          .from('api_keys')
          .select('*')
          .eq('organisation_id', organisationId)
          .eq('is_active', true)
          .order('created_at', { ascending: false })
          .limit(1)
          .single();
        
        if (!fallbackError) {
          data = fallbackData;
          error = null;
        } else {
          error = fallbackError;
        }
      }

      if (error) {
        console.error('❌ Error fetching API key:', error);
        return { data: null, error: error.message };
      }

      if (!data) {
        console.warn('⚠️ No API key found for organization:', organisationId);
        return { data: null, error: `No API key found for organization ${organisationId}. Please create an API key for this organization in the database.` };
      }

      // Cache the result
      this.cache.set(cacheKey, { data, timestamp: Date.now() });
      console.log('✅ API key cached for organization:', organisationId);
      
      return { data, error: null };
    } catch (error) {
      console.error('❌ Error in getApiKeyForOrganization:', error);
      return { data: null, error: 'Failed to fetch API key' };
    }
  }

  /**
   * Get API key for current user's organization
   */
  static async getCurrentUserApiKey(): Promise<ApiKeyResult> {
    try {
      // Get current user
      const { data: { user }, error: userError } = await supabase.auth.getUser();
      
      if (userError || !user) {
        return { data: null, error: 'User not authenticated' };
      }

      // Get user's primary organization
      const { data: userOrgData, error: userOrgError } = await supabase
        .from('user_organisations')
        .select('organisation_id')
        .eq('user_id', user.id)
        .eq('is_primary', true)
        .eq('active', true)
        .single();

      if (userOrgError || !userOrgData) {
        return { data: null, error: 'No organization found for user' };
      }

      return await this.getApiKeyForOrganization(userOrgData.organisation_id);
    } catch (error) {
      console.error('❌ Error in getCurrentUserApiKey:', error);
      return { data: null, error: 'Failed to get current user API key' };
    }
  }

  /**
   * Clear cache for an organization
   */
  static clearCache(organisationId?: string): void {
    if (organisationId) {
      this.cache.delete(`api_key_${organisationId}`);
    } else {
      this.cache.clear();
    }
  }
}
